import type {
	MouseEvent as ReactMouseEvent,
	TouchEvent as ReactTouchEvent,
	PropsWithChildren,
	HTMLAttributes,
} from 'react';
import { useRef, useEffect } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import {
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
} from '@/libraries/design/foundations/grid/src/base';

import { TabFC } from './tab-helper';
import { useTabsContext } from './tabs';

export type TabListProps = PropsWithChildren<{
	alignment?: 'start' | 'center';
	tabSize?: 'small' | 'large';
}> &
	HTMLAttributes<HTMLUListElement>;

const DraggableTabList: TabFC<TabListProps> = ({
	alignment = 'start',
	// @ts-expect-error unused
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	tabSize = 'large',
	children,
}: TabListProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const mouseDown = useRef(false);
	const startX = useRef(0);
	const scrollLeft = useRef(0);
	const canDrag = useRef(false);
	const { onKeyDown } = useTabsContext();

	const startDragging = (
		mouse?: ReactMouseEvent<HTMLUListElement>,
		touch?: ReactTouchEvent<HTMLUListElement>,
	) => {
		if (!canDrag.current) {
			return;
		}
		// Start listening for mouseup and mousemove events outside of the tab list
		document.addEventListener('mouseup', stopDragging);
		document.addEventListener('mousemove', move);

		mouseDown.current = true;
		startX.current = (touch && touch.touches[0] ? touch.touches[0].clientX : mouse?.clientX) ?? 0;
		scrollLeft.current = containerRef.current?.scrollLeft ?? 0;
	};

	const stopDragging = () => {
		if (!canDrag.current) {
			return;
		}
		// Stop listening for mouseup and mousemove events
		document.removeEventListener('mouseup', stopDragging);
		document.removeEventListener('mousemove', move);

		mouseDown.current = false;
	};

	const move = (mouse?: MouseEvent, touch?: ReactTouchEvent<HTMLUListElement>) => {
		if (!canDrag.current || !mouseDown.current || !containerRef.current) {
			return;
		}
		let dragAmount = 0;
		if (touch && touch.changedTouches[0]) {
			dragAmount = touch.changedTouches[0].clientX - startX.current;
		} else if (mouse) {
			dragAmount = mouse.clientX - startX.current;
		}

		containerRef.current.scrollLeft = scrollLeft.current - dragAmount;
	};

	useEffect(() => {
		const updateCanDrag = () => {
			if (containerRef.current) {
				const container = containerRef.current;
				canDrag.current = container.scrollWidth > container.clientWidth;
			}
		};

		const setStartingPoint = () => {
			if (containerRef.current) {
				if (canDrag.current) {
					const tabPanels = document.querySelector('.tab-panels');
					const tabPanelsStyle = tabPanels && window.getComputedStyle(tabPanels);
					containerRef.current.setAttribute(
						'style',
						`padding-left: calc(${tabPanelsStyle?.marginLeft} + ${tabPanelsStyle?.paddingLeft});`,
					);
				} else if (alignment === 'start') {
					containerRef.current.setAttribute('style', 'margin-inline: 0px; padding-inline: 0px;');
				}
			}
		};

		if (!containerRef.current) {
			return;
		}

		const observer = new ResizeObserver(() => {
			updateCanDrag();
			setStartingPoint();
		});

		observer.observe(containerRef.current);
		return () => {
			observer.disconnect();
		};
	});

	const wrapperStyle = css({
		overflowX: 'hidden',
		maxWidth: '100%',
		marginInline: 'auto',
		paddingInline: `${WCL_GRID_PADDING_SM}px`,
		'--fixed-padding': `${WCL_GRID_PADDING_SM}px`,
		[media.above.sm]: {
			paddingInline: `${WCL_GRID_PADDING_LG}px`,
			'--fixed-padding': `${WCL_GRID_PADDING_LG}px`,
		},
	});

	return (
		<div ref={containerRef} css={wrapperStyle}>
			<div
				css={tabsContainerStyles}
				// @ts-expect-error type
				onTouchStart={(e) => startDragging(undefined, e)}
				// @ts-expect-error type
				onMouseDown={(e) => startDragging(e)}
				onTouchEnd={(e) => {
					// @ts-expect-error type
					move(undefined, e);
					stopDragging();
				}}
				// @ts-expect-error type
				onTouchMove={(e) => move(undefined, e)}
				onKeyDown={onKeyDown}
				className="tab-list"
				tabIndex={-1}
				role="tablist"
			>
				{children}
			</div>
		</div>
	);
};

const tabsContainerStyles = css({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'flex-start',
	flexWrap: 'nowrap',
	padding: 0,
	margin: 0,
	listStyle: 'none',
	width: 'fit-content',
	maxWidth: '100%',
	marginBlock: token('space.200'),
	gap: token('space.300'),
});

DraggableTabList.tabsRole = 'TabList';

export { DraggableTabList };
